import React, { useState } from 'react';
import { isEmpty } from 'lodash';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import type { GridSize } from '~/components/core/Atomic/Grid/Grid';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Skeleton from '~/components/core/Atomic/Skeleton';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import AutoComplete, { createFilterOptions } from '~/components/core/Molecules/Fields/AutoComplete';
import WrapFieldWithAsterisk from '~/components/core/Molecules/WrapFieldWithAsterisk/WrapFieldWithAsterisk';
import { useCms } from '~/components/hooks/useCms';
import { ShowOnlyTextField } from '~/components/TextFieldFormik';
import type { FieldGroup } from '~/components/types/incident-configuration-types';
import type { HierarchyTreeHierarchyNode } from '~/server_shared/openapi/data-contracts';

import type { GroupValues, LevelSelection } from './hierarchyTree';
import {
  groupSelection,
  optionsForLevel,
  readStoredSelection,
  reconcileSelection,
  writeSelection,
} from './hierarchyTree';
import { useHierarchyLabels } from './useHierarchyLabels';
import { useHierarchyTree } from './useHierarchyTree';

const STALE_VALUE_MESSAGE = 'This value is no longer available';
const EMPTY_TREE_MESSAGE = 'No options are available for this claim';

// The repo carries no virtualization dependency, and adding one is out of scope for this ticket, so
// the rendered list is capped instead. Any level can exceed it -- one parent in a live source's tree
// may hold thousands of children -- so each level renders at most this many of the options matching
// what has been typed.
const MAX_RENDERED_OPTIONS = 100;
const filterOptions = createFilterOptions<HierarchyTreeHierarchyNode>({
  limit: MAX_RENDERED_OPTIONS,
  stringify: (option) => option.desc,
});

export interface HierarchyFieldGroupPickerProps {
  fieldGroup: FieldGroup;
  /** The claim's `configured_fields_values`. */
  values: Record<string, unknown>;
  /** Claim page. Exactly one of `claimId` / `policy`. */
  claimId?: number;
  /** FNOL. The policy object the form already holds, forwarded whole. */
  policy?: Record<string, unknown>;
  readOnly?: boolean;
  /**
   * FNOL renders always-editable fields; the claim page renders read-only text whose hover pencil
   * opens the levels in a dialog, the way the incident type and sub-type are edited beside them.
   * Same meaning, and same name, as `ConfiguredFields`' own `inline`.
   */
  inline?: boolean;
  /**
   * The changed-key map, cascade included, in a single call: per selection on FNOL, and once per
   * dialog Update on the claim page, where it may be awaited before the dialog closes.
   */
  onChange: (changedKeys: GroupValues) => void | Promise<void>;
}

const nodeOf = (selection: LevelSelection): HierarchyTreeHierarchyNode | null =>
  selection.id ? { id: selection.id, desc: selection.desc } : null;

export const HierarchyFieldGroupPicker: React.FC<HierarchyFieldGroupPickerProps> = ({
  fieldGroup,
  values,
  claimId,
  policy,
  readOnly = false,
  inline = false,
  onChange,
}) => {
  const { classes } = useStyles();
  const { userOrganization } = useCms();
  const [isOpen, setIsOpen] = useState(false);
  // The claim-page dialog's edits, held until Update: every key a selection there has touched.
  const [pendingKeys, setPendingKeys] = useState<GroupValues>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const subFields = fieldGroup.sub_fields;
  const storedValues = groupSelection(values?.[fieldGroup.id]);
  const valueIds = readStoredSelection(storedValues, subFields)
    .map(({ id }) => id)
    .filter(Boolean);
  const { labels, isLoading: isLoadingLabels } = useHierarchyLabels({
    groupId: fieldGroup.id,
    claimId,
    policy,
    valueIds,
  });
  const {
    index,
    isLoading,
    error,
    isEmpty: isTreeEmpty,
    retry,
  } = useHierarchyTree({
    groupId: fieldGroup.id,
    claimId,
    policy,
    enabled: !readOnly && isOpen,
  });

  // The whole group is one field for asterisk purposes, so every level carries the mark, the way
  // the surrounding configured fields do. What the mark promises is enforced at group level too:
  // `fieldGroupValidations` requires the top level, which is the deepest a schema can require
  // without knowing where a branch of the tree ends.
  const showAsterisk =
    Boolean(userOrganization.configuration?.fnol_configuration?.show_asterisk_on_mandatory_fields) &&
    Boolean(fieldGroup.mandatory) &&
    !readOnly;

  const renderShowOnly = (onEdit?: () => void) => {
    const stored = readStoredSelection(storedValues, subFields, labels);
    return (
      <>
        {subFields.map((subField, level) => (
          <Grid item xs={inline ? 6 : 3} key={subField.id}>
            <ShowOnlyTextField
              classes={classes}
              label={subField.desc}
              showOnlyValueComponent={
                isLoadingLabels && stored[level].id ? <Skeleton variant="text" width="60%" /> : stored[level].desc
              }
              onEdit={onEdit}
            />
          </Grid>
        ))}
      </>
    );
  };

  if (readOnly && inline) {
    return renderShowOnly();
  }

  const renderLevels = (
    levelValues: Record<string, unknown>,
    onSelect: (changedKeys: GroupValues) => void,
    gridXs: GridSize
  ) => {
    const selectionValues = groupSelection(levelValues?.[fieldGroup.id]);
    const stored = readStoredSelection(selectionValues, subFields, labels);
    // Before the tree arrives there is nothing to reconcile against, so the selected ID uses its cached label or ID fallback -- which is also why staleness is an edit-time signal and never a view-time one.
    const selections = index ? reconcileSelection(index, stored) : stored;

    // Level 1 draws from the roots; every level below draws from the selection above it. An empty
    // list is what a leaf, an unselected parent and a stale parent all produce, and it is exactly
    // what makes that level unreachable.
    const optionsByLevel = subFields.map((_subField, level) => {
      if (level === 0) {
        return index ? optionsForLevel(index, null) : [];
      }
      const parentId = selections[level - 1].id;
      return index && parentId ? optionsForLevel(index, parentId) : [];
    });

    const handleSelect = (level: number, node?: HierarchyTreeHierarchyNode | null) =>
      onSelect({ [fieldGroup.id]: writeSelection(subFields, selectionValues, level, node ?? null) });

    return (
      <>
        {error ? (
          <Grid item xs={12}>
            {/* The banner carries its own retry because reopening the group -- the claim page's way
                back into the fetch -- does not exist on the inline FNOL card, which only ever opens
                one. Without it a single transient failure strands the group for the whole mount. */}
            <AlertBanner
              alertType={AlertBanner.ALERT_TYPES.ERROR}
              note={
                <div className="flex items-center justify-between gap-12">
                  <span>{error}</span>
                  <Button variant="outlined" color="primary" disabled={isLoading} onClick={retry}>
                    Retry
                  </Button>
                </div>
              }
              withIcon
            />
          </Grid>
        ) : null}
        {subFields.map((subField, level) => {
          const selection = selections[level];
          const options = optionsByLevel[level];
          const value = nodeOf(selection);
          // A stored value the tree does not offer -- stale, or not yet fetched -- still has to render
          // as this field's value, so it joins its own option list rather than being dropped.
          const isValueOffTree = value !== null && !options.some((option) => option.id === value.id);
          const isDisabled = isLoading || (level > 0 && options.length === 0);

          let helperText: string | undefined;
          if (selection.isStale) {
            helperText = STALE_VALUE_MESSAGE;
          } else if (level === 0 && isTreeEmpty) {
            helperText = EMPTY_TREE_MESSAGE;
          } else if (level > 0 && !selections[level - 1].id) {
            helperText = `Select ${subFields[level - 1].desc} first`;
          }

          return (
            <Grid item xs={gridXs} key={subField.id}>
              <WrapFieldWithAsterisk showAsterisk={showAsterisk}>
                <AutoComplete<HierarchyTreeHierarchyNode>
                  id={`hierarchy_field_group.${fieldGroup.id}.${subField.id}`}
                  label={subField.desc}
                  options={isValueOffTree ? [value, ...options] : options}
                  value={value}
                  getOptionLabel={(option) => option.desc}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  getOptionDisabled={(option) => isValueOffTree && option.id === value.id}
                  filterOptions={filterOptions}
                  disabled={isDisabled}
                  loading={isLoading}
                  // A level offering exactly one child must not take it: that would write a value the
                  // user never chose, and on the claim page fire a PATCH for merely opening the group.
                  autoSelectSingleOption={false}
                  onChange={(_event, node) => handleSelect(level, node)}
                  textFieldProps={{
                    error: selection.isStale,
                    helperText,
                    // Opening the group is what fetches it -- never a claim render.
                    onFocus: () => setIsOpen(true),
                  }}
                />
              </WrapFieldWithAsterisk>
            </Grid>
          );
        })}
      </>
    );
  };

  if (inline) {
    return renderLevels(values, onChange, 6);
  }

  const closeDialog = () => setIsOpen(false);
  const openDialog = () => {
    setPendingKeys({});
    setIsOpen(true);
  };

  const handleUpdate = async () => {
    if (isEmpty(pendingKeys)) {
      closeDialog();
      return;
    }
    setIsSubmitting(true);
    try {
      await onChange(pendingKeys);
      closeDialog();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {renderShowOnly(readOnly ? undefined : openDialog)}
      {isOpen ? (
        <CardDialog
          isDialog
          open
          title={`Edit ${fieldGroup.desc}`}
          fullWidth
          maxWidth="sm"
          onClose={closeDialog}
          preventClose={isSubmitting}
          footerActions={
            <DialogFooterActions
              primaryLabel="Update"
              disabled={isSubmitting}
              primaryDisabled={isLoading}
              onClickPrimary={handleUpdate}
              onClickSecondary={closeDialog}
            />
          }
        >
          <Grid container spacing={1}>
            {renderLevels(
              { ...values, ...pendingKeys },
              (changedKeys) => setPendingKeys((previous) => ({ ...previous, ...changedKeys })),
              12
            )}
          </Grid>
        </CardDialog>
      ) : null}
    </>
  );
};
